// 24h Summary API Types

export interface MorningSummaryResponse {
  farmId: string;
  farmName: string;
  generatedAt: string;
  quickStats: MorningSummaryQuickStats;
  pondsNeedingAttention: PondAttentionItem[];
  recentAlerts: MorningSummaryAlert[];
  yesterdayActivity: MorningSummaryActivitySummary;
  aiInsights?: MorningSummaryAIInsight[];
}

export interface MorningSummaryQuickStats {
  activePondCount: number;
  attentionPondCount: number;
  alertCount24h: number;
  pondsCheckedToday: number;
  totalPondCount: number;
}

export interface PondAttentionItem {
  pondId: string;
  pondName: string;
  fishType?: string;
  primaryReason: AttentionReason;
  severity: Severity;
  details?: PondAttentionDetails;
  reasons?: AttentionReasonWithLabel[];
}

export type AttentionReason =
  | 'no_recent_test'
  | 'poor_appetite'
  | 'mortality'
  | 'ai_alert'
  | 'water_quality'
  | 'parasite'
  | 'equipment'
  | 'environmental';

export interface AttentionReasonWithLabel {
  reason: AttentionReason;
  label: string;
}

export interface PondAttentionDetails {
  lastTestAt?: string;
  hoursSinceTest?: number;
  lastAppetiteRating?: number;
  lastFeedTime?: string;
  mortalityCount?: number;
  mortalityRate?: number;
  checkedAt?: string;
  aiAlertSummary?: string;
  aiAlertId?: string;
  navigateTo?: string;
}

export interface MorningSummaryAlert {
  alertId: string;
  pondId: string;
  pondName: string;
  alertType: AlertType;
  severity: Severity;
  message: string;
  sourceRecordId: string;
  sourceRecordType: SourceRecordType;
  occurredAt: string;
}

export type AlertType =
  | 'water_quality'
  | 'parasite'
  | 'equipment'
  | 'environmental'
  | 'mortality';

export type SourceRecordType =
  | 'water_quality_test'
  | 'fish_health_check'
  | 'maintenance_check';

export type Severity = 'critical' | 'high' | 'medium' | 'low';

export interface MorningSummaryActivitySummary {
  waterQualityTests: ActivityCount;
  feedRecords: ActivityCount;
  healthChecks: ActivityCount;
  maintenanceChecks: ActivityCount;
}

export interface ActivityCount {
  completed: number;
  total: number;
}

export interface MorningSummaryAIInsight {
  analysisId: string;
  pondId: string;
  pondName: string;
  analysisType: 'root_cause' | 'health_diagnosis';
  severity: Severity;
  summary: string;
  createdAt: string;
}

// Helper functions for UI

export function getSeverityColor(severity: Severity): string {
  switch (severity) {
    case 'critical':
      return 'text-red-600 bg-red-50 border-red-200';
    case 'high':
      return 'text-amber-600 bg-amber-50 border-amber-200';
    case 'medium':
      return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'low':
      return 'text-slate-600 bg-slate-50 border-slate-200';
    default:
      return 'text-slate-600 bg-slate-50 border-slate-200';
  }
}

export function getSeverityBadgeClass(severity: Severity): string {
  switch (severity) {
    case 'critical':
      return 'bg-red-500 text-white';
    case 'high':
      return 'bg-amber-500 text-white';
    case 'medium':
      return 'bg-blue-500 text-white';
    case 'low':
      return 'bg-slate-400 text-white';
    default:
      return 'bg-slate-400 text-white';
  }
}

export function getAttentionReasonLabel(reason: AttentionReason): string {
  switch (reason) {
    case 'no_recent_test':
      return 'No recent water test';
    case 'poor_appetite':
      return 'Poor appetite';
    case 'mortality':
      return 'Mortality reported';
    case 'ai_alert':
      return 'AI alert';
    case 'water_quality':
      return 'Water quality issue';
    case 'parasite':
      return 'Parasite detected';
    case 'equipment':
      return 'Equipment issue';
    case 'environmental':
      return 'Environmental issue';
    default:
      return 'Needs attention';
  }
}

export function getNavigationTarget(reason: AttentionReason): string {
  switch (reason) {
    case 'no_recent_test':
    case 'water_quality':
      return 'water-quality';
    case 'poor_appetite':
      return 'feed';
    case 'mortality':
    case 'parasite':
      return 'fish-health';
    case 'equipment':
    case 'environmental':
      return 'maintenance';
    case 'ai_alert':
      return 'ai';
    default:
      return 'ponds';
  }
}

export function getAlertTypeLabel(alertType: AlertType): string {
  switch (alertType) {
    case 'water_quality':
      return 'Water Quality';
    case 'parasite':
      return 'Parasite';
    case 'equipment':
      return 'Equipment';
    case 'environmental':
      return 'Environmental';
    case 'mortality':
      return 'Mortality';
    default:
      return 'Alert';
  }
}
